import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const SubscriptionContext = createContext();

// Feature access matrix
const FEATURE_ACCESS = {
  // FREE modules
  expenses: 'free',
  diagnosis: 'free',
  networth: 'free',
  mynumbers: 'free',
  vision: 'free',
  analytics: 'free',
  actionplan: 'free', // To-Do List is free for all users
  
  // PREMIUM modules
  budget: 'premium',
  goals: 'premium',
  beliefs: 'premium',
  habits: 'premium',
  identity: 'premium',
  journal: 'premium',
  forecast: 'premium',
  learning: 'premium',
  wealthladder: 'premium',
  moneycycle: 'premium',
};

export const SubscriptionProvider = ({ children }) => {
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [subscriptionExpires, setSubscriptionExpires] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPremium = subscriptionTier === 'premium' || subscriptionTier === 'premium_family' || subscriptionTier === 'premium_individual';

  const API = process.env.REACT_APP_BACKEND_URL;

  // Refresh subscription status from backend
  const refreshSubscription = async () => {
    try {
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      const userId = sessionStorage.getItem('prosperMindUserId');
      if (!userId) return;

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/api/stripe/subscription-status?user_id=${userId}`, { headers });
      
      if (response.data) {
        const tier = response.data.premium_active ? 'premium' : (response.data.subscription_plan || 'free');
        setSubscriptionTier(tier);
        setSubscriptionExpires(response.data.subscription_current_period_end);
        console.log('Subscription refreshed:', tier);
      }
    } catch (error) {
      console.error('Error refreshing subscription:', error);
    }
  };

  // Check for Stripe success redirect on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeSuccess = urlParams.get('stripe_success') || urlParams.get('success');
    
    if (stripeSuccess === 'true') {
      console.log('Detected Stripe success redirect, refreshing subscription...');
      // Add small delay to allow webhook to process
      setTimeout(() => {
        refreshSubscription();
      }, 2000);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const canAccessFeature = (featureKey) => {
    const requiredTier = FEATURE_ACCESS[featureKey];
    if (!requiredTier) return true; // Unknown features are accessible
    if (requiredTier === 'free') return true;
    return isPremium;
  };

  const updateSubscription = (tier, expires) => {
    setSubscriptionTier(tier || 'free');
    setSubscriptionExpires(expires);
  };

  const value = {
    subscriptionTier,
    subscriptionExpires,
    isPremium,
    loading,
    setLoading,
    canAccessFeature,
    updateSubscription,
    refreshSubscription,
    FEATURE_ACCESS,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
